import React, { useEffect, useRef, useState } from 'react';
import { Heading } from 'react-aria-components';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { pushModal } from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';
import { integerToAmount } from 'loot-core/shared/util';
import { format as monthUtilFormat } from 'loot-core/src/shared/months';

import { useAuth } from '../../hooks/useAuth';
import { useDateFormat } from '../../hooks/useDateFormat';
import { AnimatedLoading } from '../../icons/AnimatedLoading';
import { useResponsive } from '../../ResponsiveProvider';
import { theme, styles } from '../../style';
import { gray300 } from '../../style/palette';
import { Block } from '../common/Block';
import { Button, ButtonWithLoading } from '../common/Button';
import { Card } from '../common/Card';
import { Link } from '../common/Link';
import { ModeButton } from '../common/ModeButton';
import { Text } from '../common/Text';
import { View } from '../common/View';
import BillingInfo from '../dashboard/billing/billing-info/BillingInfo';
import { usePageTitle } from '../PageTitleContext';

import BillingFinancialInstitutions from './BillingFinancialInstitutions';
import { BillingPaymentMethodChangedModal } from './BillingPaymentMethodChangedModal';

type BillingProps = {
  inManagementApp: boolean;
};

export function Billing({ inManagementApp }: BillingProps) {
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';
  const [searchParams] = useSearchParams();
  const postChangeMethod = searchParams.get('post-change-method');
  const productId = searchParams.get('productId');
  const selectedBillingInterval = searchParams.get('billingInterval');
  const { isWideWidth, isNarrowWidth } = useResponsive();
  const dispatch = useDispatch();
  const {
    billingStatus: status,
    isAuthenticated,
    refreshAuthState,
  } = useAuth();
  const productsListRef = useRef<HTMLDivElement>();

  const [products, setProducts] = useState([]);
  const [linkedInstitutions, setLinkedInstitutions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { setPageTitle } = usePageTitle();
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(
    'month',
  );
  const loadingElementRef = useRef<string | null>(null);

  useEffect(() => {
    loadInitialData();
    loadLinkedInstitutions();
    setPageTitle('Billing');
  }, []);

  useEffect(() => {
    status?.billingInterval && setBillingInterval(status?.billingInterval);
  }, [status]);

  async function loadInitialData() {
    await loadPricing();
    if (
      postChangeMethod === '1' &&
      selectedBillingInterval &&
      productId &&
      status.isFreeTrialProduct
    ) {
      await send('stripe-change-plan', {
        productId,
        billingInterval: selectedBillingInterval,
      });
      await refreshAuthState();
    }
    setIsInitialLoading(false);
  }

  async function loadPricing() {
    const products = await send('stripe-get-products');
    setProducts(products.filter(p => !p.isFreeTrialProduct));
  }

  async function loadLinkedInstitutions() {
    const institutions = await send('get-linked-institutions');
    setLinkedInstitutions(
      (institutions || []).map(ins => ({ ...ins, id: ins.connectionId })),
    );
  }

  async function onSubscribeClick(product: any) {
    loadingElementRef.current = product.productId;
    setIsLoading(true);
    try {
      await startStripeSession(product);
      loadingElementRef.current = null;
      setIsLoading(false);
    } catch (e) {
      loadingElementRef.current = null;
      setIsLoading(false);
    }
  }

  async function startStripeSession(product: any) {
    const session = await send('stripe-create-session', {
      productId: product.productId,
      billingInterval,
    });
    window.location.href = session.sessionUrl;
  }

  async function onChangeClick(product: any) {
    loadingElementRef.current = product.productId;
    setIsLoading(true);
    if (status?.isActive && status.isFreeTrialProduct) {
      await changePaymentMethod(product.productId, billingInterval);
    } else if (
      !product.perAdditionalBank &&
      status.currentUsage > product.maxInstitutions
    ) {
      dispatch(
        pushModal('confirm-generic', {
          text: `The number of connected financial institutions exceeds the limit supported by the plan you are trying to downgrade to. Please unlink some of your institutions and try again.`,
          title: 'Unable to downgrade',
          hideCancelButton: true,
          confirmBtnText: 'Understood',
          onConfirm: async () => {
            loadingElementRef.current = null;
            setIsLoading(false);
          },
          onCancel: () => {
            loadingElementRef.current = null;
            setIsLoading(false);
          },
        }),
      );
    } else {
      dispatch(
        pushModal('confirm-generic', {
          text: `You have chosen to change your billing plan to "${product.name}", which is $${integerToAmount(product[billingInterval].baseAmount)} per ${billingInterval}. Please click on "Confirm" to continue.`,
          title: 'Confirm billing plan change',
          onConfirm: async () => {
            await send('stripe-change-plan', {
              productId: product.productId,
              billingInterval,
            });
            await refreshAuthState();
            loadingElementRef.current = null;
            setIsLoading(false);
          },
          onCancel: () => {
            loadingElementRef.current = null;
            setIsLoading(false);
          },
        }),
      );
    }
  }

  async function onCancelClick() {
    loadingElementRef.current = 'cancel';
    setIsLoading(true);
    dispatch(
      pushModal('confirm-generic', {
        text: `Are you sure you want to cancel your Good Steward subscription? If you choose to continue, after the current billing cycle ends, you will lose access to all premium features, including data synchronization across your devices, and all connected bank accounts will stop importing your transactions.`,
        title: 'Confirm cancellation',
        cancelBtnText: 'No',
        confirmBtnText: "Yes, I'm sure",
        onConfirm: async () => {
          await send('stripe-cancel-plan');
          await refreshAuthState();
          loadingElementRef.current = null;
          setIsLoading(false);
        },
        onCancel: () => {
          loadingElementRef.current = null;
          setIsLoading(false);
        },
      }),
    );
  }

  async function onStopCancellationClick() {
    loadingElementRef.current = 'stop-cancel';
    setIsLoading(true);
    await send('stripe-stop-cancellation');
    await refreshAuthState();
    loadingElementRef.current = null;
    setIsLoading(false);
  }

  async function changePaymentMethod(productId?, billingInterval?) {
    loadingElementRef.current = 'change-payment';
    setIsLoading(true);
    const session = await send('stripe-create-payment-method-change-session', {
      productId,
      billingInterval,
    });
    window.location.href = session.sessionUrl;
    loadingElementRef.current = null;
    setIsLoading(false);
  }

  function renderCancel(showMessage = false) {
    let cancelButton = (
      <ButtonWithLoading
        type="bare"
        style={{ backgroundColor: theme.gsGreen200, flex: 1, marginTop: 0 }}
        loading={loadingElementRef.current === 'cancel'}
        disabled={isLoading}
        onClick={() => onCancelClick()}
      >
        Cancel
      </ButtonWithLoading>
    );
    if (status?.cancellationRequested) {
      cancelButton = (
        <View style={{ gap: 12, flex: 1 }}>
          {showMessage ? (
            <span style={{ fontSize: 13, lineHeight: '20px' }}>
              Will be canceled at{' '}
              <strong>
                {monthUtilFormat(new Date(status?.cancelAt * 1000), dateFormat)}
              </strong>
            </span>
          ) : null}
          <ButtonWithLoading
            type="primary"
            loading={loadingElementRef.current === 'stop-cancel'}
            disabled={isLoading}
            onClick={() => onStopCancellationClick()}
          >
            Revert cancellation
          </ButtonWithLoading>
        </View>
      );
    }
    return <>{cancelButton}</>;
  }

  function renderBillingIntervalToggler() {
    return (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <ModeButton
          selected={billingInterval === 'month'}
          onSelect={() => setBillingInterval('month')}
        >
          Monthly
        </ModeButton>
        <ModeButton
          selected={billingInterval === 'year'}
          onSelect={() => setBillingInterval('year')}
        >
          Yearly (2 months FREE)
        </ModeButton>
      </div>
    );
  }

  function isProductActive(product) {
    return (
      product.productId === status?.subscriptionPlan &&
      billingInterval === status?.billingInterval
    );
  }

  function getProductPriceDescription(product) {
    switch (product.name) {
      case 'Basic':
        return 'Lowest cost';
      case 'Advanced':
        return 'Most popular';
      case 'Premium':
        return 'Best value';
    }
  }

  function getProductFeautersList(product) {
    const commonFeatures = [
      <span key="max_inst" style={{ textAlign: 'left' }}>
        Bank Sync from up to{' '}
        <b>{product.maxInstitutions} Financial Institutions</b>
      </span>,
      ...(!!product[billingInterval].perAdditionalBank
        ? [
            `+ US$ ${product[billingInterval].perAdditionalBank / 100} per additional bank`,
          ]
        : []),
    ];
    switch (product.name) {
      case 'Basic':
        return [
          <span key="basic">
            Everything in{' '}
            <Link
              linkColor={theme.gsGreen700}
              to="https://www.goodsteward.io/#features"
              variant="external"
            >
              Free Tier
            </Link>
          </span>,
          ...commonFeatures,
          'Device Sync',
        ];
      case 'Advanced':
        return [
          <span key="advanced">
            Everything in <strong>Basic</strong>
          </span>,
          ...commonFeatures,
          'Priority customer support',
        ];
      case 'Premium':
        return [
          <span key="premium">
            Everything in <strong>Advanced</strong>
          </span>,
          ...commonFeatures,
          '1-on-1 customer support',
        ];
    }
  }

  function getPricingCardColors(product) {
    const isActive = isProductActive(product);
    return {
      text: isActive ? '#2b4521' : theme.gsGreen700,
      backgroundColor: isActive ? '#cee3c6' : '#FFF',
      secondaryText: isActive ? '#395c2c' : 'rgb(79, 79, 79)',
      border: isActive ? theme.gsGreen100 : '#d9d8d8',
    };
  }

  if (isInitialLoading) {
    return (
      <View
        style={{
          flex: 1,
          gap: 20,
          justifyContent: 'center',
          alignItems: 'center',
          ...styles.delayedFadeIn,
        }}
      >
        <Block style={{ marginBottom: 20, fontSize: 18 }}>
          Loading billing...
        </Block>
        <AnimatedLoading width={25} color={theme.pageTextDark} />
      </View>
    );
  }

  return (
    <>
      {inManagementApp ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            width: '100%',
            padding: '0 16px',
          }}
        >
          <Button onClick={() => window.history.back()}>&larr; Go back</Button>
          <h1 style={{ textAlign: 'center', width: '100%', fontSize: '2.5em' }}>
            Billing
          </h1>
        </div>
      ) : null}
      <BillingPaymentMethodChangedModal />

      <View
        style={{
          alignItems: 'center',
          flex: 1,
          padding: '32px 16px',
          minHeight: 'fit-Content',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '40px',
            maxWidth: '679px',
            width: '100%',
            gap: '16px',
          }}
        >
          <Card
            style={{ padding: '16px', margin: 0 }}
            childWrapperStyles={{ gap: 12 }}
          >
            <BillingInfo />
            {status?.subscriptionStartDate && !status.isFreeTrialProduct && (
              <View style={{ alignItems: 'center' }}>
                <span>
                  Your{' '}
                  {status?.stripeStatus === 'trialing' ? (
                    <strong>free trial</strong>
                  ) : (
                    'subscription'
                  )}{' '}
                  started on{' '}
                  {monthUtilFormat(
                    new Date(status.subscriptionStartDate * 1000),
                    dateFormat,
                  )}
                </span>
              </View>
            )}
            {status?.isActive ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 8,
                  paddingBottom: '2px',
                  ...(isNarrowWidth
                    ? {
                        flexDirection: 'column',
                        alignItems: 'stretch',
                      }
                    : {}),
                }}
              >
                {!status?.cancellationRequested &&
                !status.isFreeTrialProduct ? (
                  <ButtonWithLoading
                    type="primary"
                    loading={loadingElementRef.current === 'change-payment'}
                    disabled={isLoading}
                    style={{ flex: 1 }}
                    onClick={() => changePaymentMethod()}
                  >
                    Change payment method
                  </ButtonWithLoading>
                ) : null}
                {!status.isFreeTrialProduct ? renderCancel() : null}
              </div>
            ) : null}
          </Card>
          {status?.isActive && !inManagementApp ? (
            <BillingFinancialInstitutions
              financialInstitutions={linkedInstitutions}
              onReloadRequest={() => loadLinkedInstitutions()}
            />
          ) : null}
        </div>

        <View
          style={{
            alignItems: 'center',
            minHeight: 'auto',
            width: '100%',
            gap: 16,
          }}
        >
          <h1>Pricing plans</h1>
          {renderBillingIntervalToggler()}
          <div
            ref={productsListRef}
            style={{
              display: 'flex',
              alignItems: 'space-between',
              flexDirection: 'row',
              justifyContent: 'center',
              gap: 16,
              width: '100%',
              ...(!isWideWidth
                ? {
                    flexDirection: 'column',
                    width: '100%',
                  }
                : {}),
            }}
          >
            {products &&
              products.map((product, idx) => (
                <Card
                  key={idx}
                  style={{
                    minWidth: 200,
                    maxWidth: 360,
                    textAlign: 'center',
                    flex: 1,
                    ...(!isWideWidth
                      ? {
                          maxWidth: '679px',
                          width: '100%',
                          alignSelf: 'center',
                        }
                      : {}),
                    margin: 0,
                    backgroundColor:
                      getPricingCardColors(product).backgroundColor,
                  }}
                  childWrapperStyles={{ flex: 1 }}
                >
                  <View
                    style={{
                      flex: 1,
                      padding: 20,
                      minHeight: 'auto',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        paddingBottom: 20,
                        gap: 8,
                        borderBottom: `1px solid ${getPricingCardColors(product).border}`,
                      }}
                    >
                      <View style={{ alignItems: 'flex-start' }}>
                        <Heading
                          level={1}
                          style={{
                            color: getPricingCardColors(product).text,
                            fontSize: 'clamp(20px, 2vw, 24px)',
                            marginBottom: 12,
                            marginTop: 0,
                          }}
                        >
                          {product.name}
                        </Heading>
                        <Text
                          style={{
                            lineHeight: 1,
                            color: getPricingCardColors(product).secondaryText,
                          }}
                        >
                          {getProductPriceDescription(product)}
                        </Text>
                      </View>
                      <View
                        style={{
                          backgroundColor: theme.gsGreen100,
                          padding: 'clamp(11px, 1vw, 14px)',
                          borderRadius: 8,
                        }}
                      >
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 4,
                            fontSize: 'clamp(18px, 1.5vw, 22px)',
                          }}
                        >
                          <Text
                            style={{ fontSize: 'clamp(18px, 1.5vw, 22px)' }}
                          >
                            US${product[billingInterval].baseAmount / 100}
                          </Text>
                          {product.isDiscounted && (
                            <Text
                              style={{
                                textDecoration: 'line-through',
                                fontSize: 'clamp(13px, 1.2vw, 16px)',
                              }}
                            >
                              ${product[billingInterval].originalAmount / 100}
                            </Text>
                          )}
                        </View>
                        <Text
                          style={{
                            color: gray300,
                          }}
                        >
                          {`per ${billingInterval === 'month' ? 'month' : 'year'}`}
                        </Text>
                      </View>
                    </div>
                    <ul
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        margin: 0,
                        gap: 16,
                        padding: 20,
                        textAlign: 'left',
                        lineHeight: '20px',
                        borderBottom: `1px solid ${getPricingCardColors(product).border}`,
                      }}
                    >
                      {getProductFeautersList(product)?.map((p, i) => (
                        <li
                          key={i}
                          style={{
                            color: getPricingCardColors(product).text,
                            fontSize: 14,
                          }}
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        marginTop: 20,
                        flex: 1,
                      }}
                    >
                      {!isProductActive(product) ? (
                        <ButtonWithLoading
                          type="primary"
                          loading={
                            loadingElementRef.current === product.productId
                          }
                          disabled={isLoading}
                          onClick={() => {
                            if (status?.isActive && status.subscriptionPlan) {
                              onChangeClick(product);
                            } else {
                              onSubscribeClick(product);
                            }
                          }}
                        >
                          {status?.isActive && !status.isFreeTrialProduct
                            ? `Switch to ${product.name}`
                            : 'Get started'}
                        </ButtonWithLoading>
                      ) : (
                        renderCancel(true)
                      )}
                    </div>
                  </View>
                </Card>
              ))}
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              marginTop: 20,
              fontSize: 16,
              textAlign: 'center',
            }}
          >
            Need to connect to more than 25 financial institutions?{' '}
            <a href="mailto:support@goodsteward.io?subject=Custom%20plan">
              Contact us
            </a>
          </div>
        </View>
      </View>
    </>
  );
}
